import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation } from 'react-router';
import LoadingDots from './LoadingDots/LoadingDots';
import SearchError from './SearchError/SearchError';
import NoResults from './NoResults/NoResults';
import Location from './Location/Location';
import Icon from '@mdi/react';
import { mdiMagnify } from '@mdi/js';
import { DEVICE_VALID_MODES } from './Search.mode';
import mapModeIsValid from '../validateModes';
import useDeviceType from 'client/utils/helpers/DeviceType/useDeviceType';
import styles from './Search.module.scss';
import useSearchController, { STATUSES, PAN_STATUSES } from './useSearchController';

const Search = ({ mapMode, ...props }) => {
    const intl = useIntl();
    const location = useLocation();
    const DEVICE_TYPE = useDeviceType();
    const VALID_MODES = DEVICE_VALID_MODES[DEVICE_TYPE];

    const rootRef = useRef(null);

    const {
        refs: { inputRef, searchBtnRef, resultsRef },
        state: {
            searchTerm,
            errorMessage,
            results,
            status,
            resultsHidden,
            highlightedIndex,
            panStatus
        },
        derived: { open, disabled },
        actions: {
            hideSearch,
            handleOnChange,
            handleInputFocus,
            handleSearchKeypress,
            handleResultsFocus,
            handleSearchBtnClick
        },
        selectFeature
    } = useSearchController();

    // Click outside closes the whole widget (input + button + results)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                hideSearch();
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [hideSearch]);

    // DOM focus stays in the view layer
    useEffect(() => {
        if (location.state?.searchTerm) {
            inputRef.current?.focus?.();
        }
    }, [inputRef, location.state?.searchTerm]);

    // Base testid is preserved at idle; the pan status is appended once a search result has been selected
    const searchTestId = panStatus === PAN_STATUSES.IDLE ? 'mapPage.search' : `mapPage.search.${panStatus}`;

    return mapModeIsValid(mapMode, VALID_MODES) &&
        <div ref={rootRef} className={styles.searchBar} data-testid={searchTestId}>
            <div data-testid="mapToolBar.search" className={['govuk-input__wrapper', styles.search].join(' ')}>
                <div className={['govuk-input__prefix', styles.icon].join(' ')} aria-hidden="true">
                    <Icon path={mdiMagnify} size={1} />
                </div>
                <input
                    ref={inputRef}
                    className={['govuk-input', errorMessage ? 'govuk-input--error' : null, styles.input].join(' ')}
                    onChange={handleOnChange}
                    onKeyDown={handleSearchKeypress}
                    aria-label={intl.formatMessage({ id: 'search.button' })}
                    onFocus={handleInputFocus}
                    spellCheck="false"
                    data-testid={props['data-testid'] ? props['data-testid'] + '-input' : null}
                    tabIndex={0}
                    value={searchTerm}
                />
                <button
                    id="searchButton"
                    className={['govuk-input__suffix', styles.searchBtn, disabled ? styles.searchBtnDisabled : null].join(' ')}
                    ref={searchBtnRef}
                    onClick={handleSearchBtnClick}
                    onKeyDown={handleSearchKeypress}
                    disabled={disabled}
                    aria-disabled={disabled}
                    data-testid={props['data-testid'] ? props['data-testid'] + '-button' : null}
                >
                    {intl.formatMessage({ id: 'search.button' })}
                </button>
            </div>
            {open && !resultsHidden &&
                <div
                    ref={resultsRef}
                    className={styles.searchResults}
                    data-testid="mapPage.search.results"
                    data-status={status}
                    tabIndex={0}
                    onKeyDown={handleSearchKeypress}
                    onFocus={handleResultsFocus}
                    role="region"
                    aria-label={intl.formatMessage({ id: 'search.results' })}
                >
                    {status === STATUSES.SEARCHING && <LoadingDots />}
                    {status === STATUSES.ERROR && <SearchError body={errorMessage} />}
                    {status === STATUSES.NO_RESULTS && <NoResults />}
                    {status === STATUSES.IDLE && results?.length > 0 &&
                        results.map((feature, index) => {
                            return <Location
                                key={feature?.properties?.displayText}
                                feature={feature}
                                isSelected={index === highlightedIndex}
                                onSelect={selectFeature}
                                data-testid={props['data-testid'] ? `${props['data-testid']}.location.${index}` : null}
                            />;
                        })}
                </div>
            }
        </div>;
};

Search.propTypes = {
    mapMode: PropTypes.array,
    'data-testid': PropTypes.string
};

const mapStateToProps = state => {
    const { map } = state;
    return {
        mapMode: map?.mode || []
    };
};

export default connect(mapStateToProps)(Search);
